import { Card, Icon, Tag, Text } from "@ttc3k/trekker";
import { Box, BoxProps, Flex, styled } from "styled-system/jsx";
import { eventWidgetCardRecipe, EventWidgetCardRecipeProps } from "./recipe";
import { CloudinaryImage } from "shared/components/atoms";
import { css } from "styled-system/css";
import {
  limitFill,
  scale,
} from "@cloudinary/transformation-builder-sdk/actions/resize";
import { EventManyQuery, EventWithOccurrences } from "gql/generated";
import {
  getFormattedEventDetailsLink,
  getMultilingualString,
  isLandscape,
} from "shared/utils";
import { useEventWidgetCard } from "./useEventWidgetCard";
import { useEventsWidgetContext } from "widgets/events/components/EventsContext";
import { Repeat } from "iconoir-react";
import { autoGravity } from "@cloudinary/transformation-builder-sdk/qualifiers/gravity";
import { PostHogCustomEvent, usePostHog } from "shared/hooks/usePostHog";

type EventWidgetCardProps = {
  event: EventManyQuery["eventMany"][0];
  cardSurfaceProps?: BoxProps;
} & EventWidgetCardRecipeProps;

export const EventWidgetCard = ({
  event,
  cardSurfaceProps,
  orientation,
}: EventWidgetCardProps) => {
  const {
    eventDetailsLink,
    eventDetailsDisplayMode,
    lang,
    locale,
    setSelectedEvent,
    viewOnly,
  } = useEventsWidgetContext();
  const classes = eventWidgetCardRecipe({ orientation });
  const isLandscapeOrientation = orientation === "landscape";
  const { organizationId, widgetId } = useEventsWidgetContext();
  const { trackPostHogEvent } = usePostHog();

  const { dateInfo } = useEventWidgetCard(event, lang, locale);

  // When clicked, we either open the event detains in a new tab following the provided link format, or we open the slideout
  const handleEventDetailsLinkClick = (event: EventWithOccurrences) => {
    if (eventDetailsLink && eventDetailsDisplayMode === "link") {
      if (!viewOnly) {
        trackPostHogEvent(PostHogCustomEvent.ExpandedDetailsClick, {
          organizationId: organizationId,
          widgetId: widgetId,
          eventId: event._id,
        });
      }
      window.open(
        getFormattedEventDetailsLink(event, eventDetailsLink!),
        "_blank",
      );
    } else {
      if (!viewOnly) {
        trackPostHogEvent(PostHogCustomEvent.DetailsClick, {
          organizationId: organizationId,
          widgetId: widgetId,
          eventId: event._id,
        });
      }
      setSelectedEvent(event);
    }
  };

  // We handle the space or enter key to open the event details in a new tab following the provided link format, or we open the slideout
  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === " " || e.key === "Enter") {
      handleEventDetailsLinkClick(event as EventWithOccurrences);
    }
  };

  const heroImage = event.images[0];
  const heroIsLandscape = isLandscape(heroImage);
  const heroHasText = heroImage?.detectedObjects?.hasText;

  /**
   * For landscape cards, the images are square, so we scale them by their height (landscape) or width (portrait)
   * - Cloudinary will pick the "best" part of the image to display
   * - If the image has text, it will be set to contain
   * If the image has text, we scale it to the width or height (the widget is a guess as the card grows in width based on screen size)
   * For non-text images, we use the limitFill to have Cloudinary pick the "best" part of the image to display
   * @returns
   */
  const getHeroResize = () =>
    orientation === "landscape"
      ? heroIsLandscape
        ? scale().height(160)
        : scale().width(160)
      : heroHasText
        ? heroIsLandscape
          ? scale().height(200)
          : scale().width(280)
        : heroIsLandscape
          ? limitFill().height(200).width(280).gravity(autoGravity())
          : limitFill().width(280).height(200).gravity(autoGravity());

  return (
    <Card.Surface
      part={"card__surface"}
      className={classes.surface}
      tabIndex="0"
      {...cardSurfaceProps}
      onClick={() => handleEventDetailsLinkClick(event as EventWithOccurrences)}
      onKeyUp={(e: React.KeyboardEvent) => handleKeyUp(e)}
    >
      <Card.Body part={"card__body"} className={classes.body}>
        <Flex part={"card___tag_and_title"} className={classes.dateAndTitle}>
          <Tag part={"card__tag"} visual="primary" mode="light">
            <styled.span
              display="flex"
              flexDir="row"
              alignItems="center"
              gap="100"
            >
              <span>
                {dateInfo.startDate}
                {dateInfo.otherDates.length > 0 && (
                  <span>
                    {dateInfo.otherDates.slice(0, 2).map(date => (
                      <span key={date}>
                        <styled.span color="text.light"> • </styled.span>
                        {date}
                      </span>
                    ))}
                    {dateInfo.otherDates.length > 2 && "..."}
                  </span>
                )}
              </span>
              {dateInfo.showRepeat && <Icon Element={Repeat} />}
            </styled.span>
          </Tag>

          <Text
            part={"card__title"}
            visual={"bodySemiBold"}
            lineClamp={2}
            color={"text.dark"}
            h="48px"
          >
            {getMultilingualString(event.title)}
          </Text>
        </Flex>

        <Text
          part={"card__venue"}
          visual={"smallSemiBold"}
          lineClamp="1"
          color={"text.light"}
          justifySelf="flex-end"
        >
          {getMultilingualString(event.eventLocation.venue)}
        </Text>
      </Card.Body>
      <Box part={"card__hero"} className={classes.hero}>
        <CloudinaryImage
          includeBgBlurHash={true}
          image={heroImage}
          resize={getHeroResize()}
          imageStyleProps={{
            className: css({
              backgroundColor: "#FFF",
              aspectRatio: isLandscapeOrientation ? "square" : "7 / 5",
              objectFit: heroHasText ? "contain" : "cover",
              h: isLandscapeOrientation ? "160px" : "",
              w: isLandscapeOrientation ? "160px" : "",
              borderRadius: 0,
              border: "none !important",
            }),
          }}
        />
      </Box>
    </Card.Surface>
  );
};
